import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Pencil, Trash2, Plus, Save, X, Search, AlertCircle, Clock } from 'lucide-react';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import DateInput from '../components/DateInput';
import { useLanguage } from '../context/LanguageContext';

// What every punch of the batch shares: they are all captured for the SAME employee, at the same
// facility, with one reason behind the whole capture.
const EMPTY_FORM = {
  badgenum_ts: '',
  facility_ts: '',
};

// One punch of the batch. Creating opens with a single line and the user adds as many as needed
// (a whole week of punches, an IN/OUT pair, whatever) instead of reopening the modal each time.
const EMPTY_LINE = {
  date_ts: '',
  hour_ts: '',
  action_ts: 'IN',
  door_num_ts: 0,
  door_name_ts: '',
};

// Columns of one punch line: date | time | action | door | remove.
const LINE_GRID = { gridTemplateColumns: '1.05fr 0.85fr 0.8fr 1.9fr 28px' };

// Un punch que ya existe en ese dia: hora | puerta | tipo | origen | corregir.
const EXISTING_GRID = { gridTemplateColumns: '82px 1.4fr 1fr auto 92px' };

// The Monday of the week a date falls in. Punch changes are recalculated by week, so several
// punches of the same week only need one recalculation.
function mondayOf(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const dow = d.getDay();
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
  return d.toISOString().substring(0, 10);
}

function fmtDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d.substring(0, 10) : d;
  return s;
}
function fmtHour(h) {
  if (!h) return '';
  return String(h).substring(0, 8);
}

export default function Timestamps() {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [doors, setDoors] = useState([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [onlyLate, setOnlyLate] = useState(false);

  // `badge` se llena solo al elegir un empleado del autocompletado: es filtro EXACTO, mientras que
  // `search` es texto libre y va por LIKE (buscar "21" tambien traeria 121 y 210). Nunca viajan los
  // dos a la vez — ver fetchData.
  const [filters, setFilters] = useState({ facility: '', dateFrom: '', dateTo: '', search: '', badge: '' });
  const [refetchTick, setRefetchTick] = useState(0);

  // Autocompletado de la barra de filtros (el del modal es aparte).
  const [filterEmpResults, setFilterEmpResults] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [pickedFilterEmp, setPickedFilterEmp] = useState(null);
  const filterSearchTimeout = useRef(null);
  const filterDropdownRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // The punches being captured. Always at least one; editing works on exactly one.
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Employee search inside modal
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [empSelected, setEmpSelected] = useState(null);
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const empSearchTimeout = useRef(null);
  const empDropdownRef = useRef(null);

  // Los punches que YA existen para el empleado en los dias que se estan capturando.
  // Capturar a ciegas es la trampa cara del modulo: el bloqueo del import es por hora EXACTA, asi
  // que un punch a mano a las 08:00 no reemplaza al 07:45 que el archivo trae — conviven, y el
  // calculo se queda con el mas temprano. Viendo lo que ya hay se decide bien: corregir el que
  // esta mal (eso si bloquea la copia del archivo) o capturar uno nuevo a sabiendas.
  const [dayPunches, setDayPunches] = useState({});
  const [loadingDayPunches, setLoadingDayPunches] = useState(false);

  const fetchDoors = useCallback(async () => {
    try {
      const res = await axios.get('/api/doors');
      setDoors(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch { /* ignore */ }
  }, []);

  const fetchData = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum, pageSize, includeDeleted: includeDeleted ? 1 : 0, onlyLate: onlyLate ? 1 : 0 };
      Object.keys(filters).forEach(k => { if (filters[k]) params[k] = filters[k]; });
      const res = await axios.get('/api/timestamps', { params });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || 'Error loading timestamps.' });
    }
    setLoading(false);
  }, [filters, page, pageSize, includeDeleted, onlyLate, t]);

  useEffect(() => { fetchDoors(); }, [fetchDoors]);
  useEffect(() => { fetchData(1); /* eslint-disable-next-line */ }, [refetchTick, includeDeleted, onlyLate]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (empDropdownRef.current && !empDropdownRef.current.contains(e.target)) setShowEmpDropdown(false);
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) setShowFilterDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Al escribir en el filtro van apareciendo los empleados. Se busca con retardo para no lanzar una
  // consulta por tecla; la tabla no se toca hasta elegir a alguien o aplicar.
  const onFilterSearch = (v) => {
    setFilters(f => ({ ...f, search: v, badge: '' }));
    setPickedFilterEmp(null);
    if (filterSearchTimeout.current) clearTimeout(filterSearchTimeout.current);
    if (!v) { setFilterEmpResults([]); setShowFilterDropdown(false); return; }
    filterSearchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get('/api/employees', { params: { search: v, limit: 8 } });
        setFilterEmpResults(res.data.data || []);
        setShowFilterDropdown(true);
      } catch { setFilterEmpResults([]); }
    }, 300);
  };

  // Elegir a alguien filtra por su badge EXACTO, arrastra su facilidad y busca de una vez: es lo
  // que se queria hacer al escribir el nombre, sin un paso extra.
  const onFilterEmpPick = (emp) => {
    setFilters(f => ({
      ...f,
      search: '',
      badge: String(emp.badge_num_emp),
      facility: emp.location_emp || f.facility,
    }));
    setPickedFilterEmp(emp);
    setShowFilterDropdown(false);
    setFilterEmpResults([]);
    setPage(1);
    setRefetchTick(x => x + 1);
  };

  const clearFilterEmp = () => {
    setPickedFilterEmp(null);
    setFilters(f => ({ ...f, badge: '', search: '' }));
    setPage(1);
    setRefetchTick(x => x + 1);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setLines([{ ...EMPTY_LINE }]);
    setReason('');
    setEmpSearch('');
    setEmpSelected(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      badgenum_ts: row.badgenum_ts,
      facility_ts: row.facility_ts,
    });
    setLines([{
      date_ts: fmtDate(row.date_ts),
      hour_ts: fmtHour(row.hour_ts),
      action_ts: row.action_ts,
      door_num_ts: row.door_num_ts,
      door_name_ts: row.door_name_ts || '',
    }]);
    setReason('');
    setEmpSearch(`${row.name_emp || ''} ${row.last_name_emp || ''} (${row.badgenum_ts})`.trim());
    setEmpSelected({ badge_num_emp: row.badgenum_ts, name_emp: row.name_emp, last_name_emp: row.last_name_emp });
    setModalOpen(true);
  };

  const searchEmployees = async (q) => {
    if (!q || q.length < 1) { setEmpResults([]); setShowEmpDropdown(false); return; }
    try {
      const res = await axios.get('/api/employees', { params: { search: q, limit: 10 } });
      setEmpResults(res.data.data || []);
      setShowEmpDropdown(true);
    } catch { setEmpResults([]); }
  };

  const onEmpSearch = (v) => {
    setEmpSearch(v);
    setEmpSelected(null);
    setForm(f => ({ ...f, badgenum_ts: '' }));
    if (empSearchTimeout.current) clearTimeout(empSearchTimeout.current);
    empSearchTimeout.current = setTimeout(() => searchEmployees(v), 300);
  };

  const onEmpPick = (emp) => {
    setEmpSelected(emp);
    setEmpSearch(`${emp.name_emp} ${emp.last_name_emp} (${emp.badge_num_emp})`);
    setForm(f => ({ ...f, badgenum_ts: emp.badge_num_emp, facility_ts: emp.location_emp || f.facility_ts }));
    setShowEmpDropdown(false);
  };

  // Las fechas distintas que el lote esta tocando, ordenadas. Como string para que el efecto de
  // abajo no se dispare en cada tecleo que no cambie el conjunto.
  const captureDatesKey = useMemo(
    () => [...new Set(lines.map(l => l.date_ts).filter(Boolean))].sort().join(','),
    [lines]);

  useEffect(() => {
    // Solo al capturar: editando ya se esta apuntando a un punch concreto.
    if (!modalOpen || editing || !form.badgenum_ts || !captureDatesKey) { setDayPunches({}); return; }
    const dates = captureDatesKey.split(',');
    let cancelled = false;
    (async () => {
      setLoadingDayPunches(true);
      try {
        const res = await axios.get('/api/timestamps', {
          params: {
            badge: form.badgenum_ts,
            dateFrom: dates[0], dateTo: dates[dates.length - 1],
            pageSize: 200,
          },
        });
        if (cancelled) return;
        const wanted = new Set(dates);
        const byDate = {};
        for (const r of (res.data?.data || [])) {
          const d = fmtDate(r.date_ts);
          if (!wanted.has(d)) continue; // el rango puede traer dias intermedios que nadie pidio
          (byDate[d] = byDate[d] || []).push(r);
        }
        for (const d of Object.keys(byDate)) byDate[d].sort((a, b) => fmtHour(a.hour_ts).localeCompare(fmtHour(b.hour_ts)));
        setDayPunches(byDate);
      } catch {
        if (!cancelled) setDayPunches({});
      }
      if (!cancelled) setLoadingDayPunches(false);
    })();
    return () => { cancelled = true; };
  }, [modalOpen, editing, form.badgenum_ts, captureDatesKey]);

  const existingCount = useMemo(
    () => Object.values(dayPunches).reduce((n, arr) => n + arr.length, 0), [dayPunches]);

  // Pasar de capturar a corregir uno de los punches que ya existen. Es el camino que de verdad
  // arregla el dia: editar un punch importado guarda su identidad original (src_*) y con eso el
  // archivo deja de reinsertarlo en la siguiente carga.
  const correctExisting = async (row) => {
    if (lines.some(l => l.hour_ts)) {
      const r = await Swal.fire({
        icon: 'question',
        title: t('ts.switchToEditTitle'),
        text: t('ts.switchToEditMsg'),
        showCancelButton: true,
        confirmButtonText: t('ts.switchToEditYes'),
        cancelButtonText: t('common.cancel'),
      });
      if (!r.isConfirmed) return;
    }
    openEdit(row);
  };

  const setLine = (i, patch) => setLines(ls => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  // Las puertas que se pueden elegir: activas y de la instalacion del formulario. Va aqui arriba
  // porque onDoorPick tambien busca en ella, no solo el <select>.
  const filteredDoors = doors.filter(d =>
    d.status_door === 'Active' &&
    (!form.facility_ts || !d.facility || d.facility === form.facility_ts)
  );

  // La puerta se identifica por NOMBRE, no por numero. Las cuatro puertas activas de AUSWH tienen
  // number_door = 0 (y LOD de MIA tambien), asi que buscar por numero devolvia siempre la primera
  // que coincidiera: en Austin daba igual cual eligieras, se guardaba otra. El nombre ademas es lo
  // que usa el calculo (classifyDoor) y lo que buscan las consultas (WHERE name_door = ...), o sea
  // el identificador de verdad. Se busca dentro de las puertas ya filtradas por instalacion para
  // que un nombre repetido en dos plantas no se cruce.
  const onDoorPick = (i, nameDoor) => {
    const d = filteredDoors.find(x => x.name_door === nameDoor);
    setLine(i, d
      ? { door_num_ts: d.number_door || 0, door_name_ts: d.name_door }
      : { door_num_ts: 0, door_name_ts: '' });
  };

  // A new line starts as a copy of the last one with the time cleared: capturing a week of punches
  // repeats the door and the action, and the IN/OUT pair of a day repeats the date too.
  const addLine = () => setLines(ls => {
    const last = ls[ls.length - 1] || EMPTY_LINE;
    return [...ls, { ...last, hour_ts: '' }];
  });

  const removeLine = (i) => setLines(ls => (ls.length <= 1 ? ls : ls.filter((_, idx) => idx !== i)));

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.badgenum_ts) { Swal.fire({ icon: 'warning', title: t('ts.badgeReq') || 'Select an employee.' }); return; }
    if (!form.facility_ts) { Swal.fire({ icon: 'warning', title: t('ts.fieldsReq') || 'Fill all required fields.' }); return; }

    // A line with no time cannot become a punch. Adding a line and leaving it unfilled should not
    // block the save — it is simply not saved (said in the hint under the list). The original index
    // is kept so a warning names the line the user is actually looking at.
    const entries = lines.map((l, i) => ({ l, i })).filter(({ l }) => l.hour_ts);
    if (entries.length === 0) {
      Swal.fire({ icon: 'warning', title: t('ts.fieldsReq') || 'Fill all required fields.' });
      return;
    }

    // Each punch of the batch is validated on its own, naming the line that is incomplete — with
    // several lines on screen, "fill all required fields" alone would not say which one.
    const lineLabel = (i) => (lines.length > 1 ? ` (${t('ts.punch') || 'Punch'} ${i + 1})` : '');
    for (const { l, i } of entries) {
      if (!l.date_ts || !l.action_ts) {
        Swal.fire({ icon: 'warning', title: (t('ts.fieldsReq') || 'Fill all required fields.') + lineLabel(i) });
        return;
      }
      // The calculation classifies punches by DOOR (clock-in / clock-out / lunch / lobby): a punch
      // saved without one lands in no bucket and never gets picked as the clock-in of a day that
      // already has imported punches — it would look saved but change nothing.
      if (!l.door_name_ts) {
        Swal.fire({
          icon: 'warning',
          title: (t('ts.doorReq') || 'Select a door.') + lineLabel(i),
          text: t('ts.doorReqWhy') || 'Hours are calculated from the door, so a punch without one is ignored.',
        });
        return;
      }
    }
    // Two identical punches would be indistinguishable in the table and the calculation would just
    // pick one of them.
    const seen = new Set();
    for (const { l, i } of entries) {
      const k = `${l.date_ts}|${l.hour_ts}`;
      if (seen.has(k)) {
        Swal.fire({ icon: 'warning', title: (t('ts.dupLine') || 'Two punches have the same date and time.') + lineLabel(i) });
        return;
      }
      seen.add(k);
    }
    // Y contra los punches que YA existen en esos dias — los mismos que el aviso de arriba lista.
    // Antes solo se comparaban los renglones entre si, asi que capturar uno identico a uno ya
    // guardado pasaba derecho: quedaban dos filas iguales y el calculo se quedaba con una de las dos
    // sin decir cual. Se compara con la hora normalizada porque el input da HH:MM y la tabla
    // devuelve HH:MM:SS.
    if (!editing) {
      const hhmmss = (h) => {
        const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(h || '').trim());
        return m ? `${m[1].padStart(2, '0')}:${m[2]}:${m[3] || '00'}` : String(h || '');
      };
      for (const { l, i } of entries) {
        const existing = dayPunches[l.date_ts] || [];
        if (existing.some(p => hhmmss(p.hour_ts) === hhmmss(l.hour_ts))) {
          Swal.fire({
            icon: 'warning',
            title: (t('ts.dupExisting') || 'That punch already exists on that day.') + lineLabel(i),
            text: t('ts.dupExistingWhy') || 'Use "Correct" on the existing punch instead of capturing another one at the same time.',
          });
          return;
        }
      }
    }
    // The reason is only required to EDIT a punch — correcting one has to justify itself. Capturing
    // a new one does not: the placeholder has always said it is optional there, and the audit log
    // records the insert either way.
    if (editing && !reason.trim()) {
      Swal.fire({ icon: 'warning', title: t('ts.reasonReq') || 'Reason is required.' });
      return;
    }

    setSaving(true);
    try {
      const shared = { badgenum_ts: form.badgenum_ts, facility_ts: form.facility_ts };
      if (editing) {
        await axios.put(`/api/timestamps/${editing.id_ts}`, { ...lines[0], ...shared, reason });
      } else {
        // One request for the whole batch: it lands whole or not at all, and each affected week is
        // recalculated once instead of once per punch.
        await axios.post('/api/timestamps/bulk', {
          punches: entries.map(({ l }) => ({ ...l, ...shared })), reason,
        }, { timeout: 300000 });
      }

      // Auto-recalculate affected weeks. The calculate endpoint expands a single date to its full
      // Mon-Sun week, so one call per distinct week+facility covers every punch of the batch.
      const targets = new Map();
      const addTarget = (date, facility) => {
        if (!date || !facility) return;
        const wk = mondayOf(date);
        targets.set(`${wk}|${facility}`, { date: wk, facility });
      };
      for (const { l } of entries) addTarget(l.date_ts, form.facility_ts);
      if (editing) addTarget(fmtDate(editing.date_ts), editing.facility_ts);
      try {
        for (const target of targets.values()) {
          await axios.post('/api/timecards/calculate',
            { dateFrom: target.date, dateTo: target.date, facility: target.facility },
            { timeout: 300000 });
        }
      } catch (recalcErr) {
        console.error('Auto-recalculate failed after save:', recalcErr);
      }

      setModalOpen(false);
      Swal.fire({
        icon: 'success',
        title: !editing && entries.length > 1
          ? `${entries.length} ${t('ts.punchesSaved') || 'punches saved'}`
          : (t('common.saved') || 'Saved'),
        timer: 1500, showConfirmButton: false,
      });
      fetchData(page);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || 'Error saving.' });
    }
    setSaving(false);
  };

  const handleDelete = async (row) => {
    const { value: reasonInput } = await Swal.fire({
      title: t('ts.deleteTitle') || 'Delete punch?',
      html: `Badge <b>${row.badgenum_ts}</b> — ${row.date_ts} ${fmtHour(row.hour_ts)} (${row.action_ts})`,
      input: 'textarea',
      inputLabel: t('ts.reasonLabel') || 'Reason (required)',
      inputPlaceholder: t('ts.reasonPh') || 'Why is this being deleted?',
      inputValidator: (v) => !v?.trim() && (t('ts.reasonReq') || 'Reason is required.'),
      showCancelButton: true,
      confirmButtonText: t('common.delete') || 'Delete',
      cancelButtonText: t('common.cancel') || 'Cancel',
      confirmButtonColor: '#ef4444',
    });
    if (!reasonInput) return;
    try {
      await axios.delete(`/api/timestamps/${row.id_ts}`, { data: { reason: reasonInput } });
      Swal.fire({ icon: 'success', title: t('common.deleted') || 'Deleted', timer: 1500, showConfirmButton: false });
      fetchData(page);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || 'Error deleting.' });
    }
  };

  const applyFilters = () => setRefetchTick(t => t + 1);
  const clearFilters = () => {
    setFilters({ facility: '', dateFrom: '', dateTo: '', search: '', badge: '' });
    setPickedFilterEmp(null);
    setFilterEmpResults([]);
    setRefetchTick(t => t + 1);
  };

  // Only the lines that carry a time will be saved (see handleSave), so that is what the button
  // and the header count.
  const filledLines = lines.filter(l => l.hour_ts).length;

  return (
    <div>
      <div className="page-header">
        <h1>{t('ts.title') || 'Punches'}</h1>
        <div className="page-header-actions">
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} onClick={openCreate}>
            <Plus size={15} style={{ marginRight: 6 }} />{t('ts.addManual') || 'Add manual punch'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <div style={{ position: 'relative' }} ref={filterDropdownRef}>
            <label className="form-label" style={{ fontSize: 12 }}>{t('common.search') || 'Search'}</label>
            {pickedFilterEmp ? (
              <div className="picked-emp" style={{ padding: '6px 10px' }}>
                <span className="badge badge-info" style={{ fontSize: 10 }}>{pickedFilterEmp.badge_num_emp}</span>
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  {pickedFilterEmp.name_emp} {pickedFilterEmp.last_name_emp}
                </span>
                <button type="button" className="row-ghost-btn" style={{ marginLeft: 'auto' }}
                  title={t('ts.clearEmpFilter')} onClick={clearFilterEmp}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <input className="form-input" value={filters.search}
                onChange={e => onFilterSearch(e.target.value)}
                onFocus={() => { if (filterEmpResults.length > 0) setShowFilterDropdown(true); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setShowFilterDropdown(false); applyFilters(); } }}
                placeholder={t('ts.searchPh') || 'Name or badge'} />
            )}
            {showFilterDropdown && filterEmpResults.length > 0 && !pickedFilterEmp && (
              <div className="export-search-dropdown" style={{ left: 0, right: 0, width: 'auto', zIndex: 100 }}>
                {filterEmpResults.map(emp => (
                  <div key={emp.id_emp} className="export-search-item" onClick={() => onFilterEmpPick(emp)}>
                    <span style={{ fontWeight: 600 }}>{emp.badge_num_emp}</span>
                    <span style={{ marginLeft: 8 }}>{emp.name_emp} {emp.last_name_emp}</span>
                    <span className="badge badge-info" style={{ marginLeft: 'auto', fontSize: 10 }}>{emp.location_emp}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="form-label" style={{ fontSize: 12 }}>{t('hrs.facility') || 'Facility'}</label>
            <select className="form-input" value={filters.facility} onChange={e => setFilters(f => ({ ...f, facility: e.target.value }))}>
              <option value="">{t('common.all') || 'All'}</option>
              <option value="MIA">MIA</option>
              <option value="AUSWH">AUSWH</option>
            </select>
          </div>
          <div>
            <label className="form-label" style={{ fontSize: 12 }}>{t('common.from') || 'From'}</label>
            <DateInput className="form-input" value={filters.dateFrom} onChange={v => setFilters(f => ({ ...f, dateFrom: v }))} />
          </div>
          <div>
            <label className="form-label" style={{ fontSize: 12 }}>{t('common.to') || 'To'}</label>
            <DateInput className="form-input" value={filters.dateTo} onChange={v => setFilters(f => ({ ...f, dateTo: v }))} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} onClick={applyFilters} disabled={loading}>
            <Search size={14} style={{ marginRight: 6 }} />{t('common.apply') || 'Apply'}
          </button>
          <button className="btn-secondary" style={{ width: 'auto', padding: '8px 20px' }} onClick={clearFilters}>
            <X size={14} style={{ marginRight: 6 }} />{t('common.clear') || 'Clear'}
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeDeleted} onChange={e => setIncludeDeleted(e.target.checked)} />
            {t('ts.showDeleted') || 'Show deleted'}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: onlyLate ? 'var(--warning)' : undefined }}>
            <input type="checkbox" checked={onlyLate} onChange={e => setOnlyLate(e.target.checked)} />
            <Clock size={14} style={{ color: 'var(--warning)' }} />
            {t('ts.onlyLate') || 'Only late'}
          </label>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>{total} {t('ts.entries') || 'entries'}</span>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{t('common.date') || 'Date'}</th>
                <th>{t('common.time') || 'Time'}</th>
                <th>{t('common.badge') || 'Badge'}</th>
                <th>{t('hrs.employee') || 'Employee'}</th>
                <th>{t('rec.door') || 'Door'}</th>
                <th>{t('rec.action') || 'Action'}</th>
                <th>{t('hrs.facility') || 'Facility'}</th>
                <th>{t('common.status') || 'Status'}</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>{t('common.loading') || 'Loading…'}</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>{t('ts.empty') || 'No punches.'}</td></tr>
              )}
              {!loading && rows.map(r => {
                const isIncomplete = r.status_ts === 0 && r.incomplete_day;
                const isLate = r.status_ts === 0 && r.is_late;
                const veryLate = isLate && Number(r.late_minutes) > 120;
                const missingLabel = r.missing_type === 'OUT'
                  ? (t('ts.missingOut') || 'Missing clock-out for this day')
                  : (t('ts.missingIn') || 'Missing clock-in for this day');
                const lateLabel = isLate
                  ? `${t('ts.lateTooltip') || 'Late arrival'} (${r.late_minutes} min)`
                  : '';
                return (
                <tr key={r.id_ts} style={{
                  opacity: r.status_ts === 1 ? 0.45 : 1,
                  background: isIncomplete
                    ? 'rgba(239, 68, 68, 0.08)'
                    : veryLate
                      ? 'rgba(230, 126, 34, 0.14)'
                      : isLate
                        ? 'rgba(245, 158, 11, 0.10)'
                        : undefined,
                }}>
                  <td>{fmtDate(r.date_ts)}</td>
                  <td style={{ fontFamily: 'monospace' }}>{fmtHour(r.hour_ts)}</td>
                  <td>{r.badgenum_ts}</td>
                  <td>{r.name_emp ? `${r.name_emp} ${r.last_name_emp || ''}` : '-'}</td>
                  <td>{r.door_name_ts || '-'}</td>
                  <td>
                    <span className="badge badge-info">{r.action_ts}</span>
                    {isIncomplete && (
                      <AlertCircle
                        size={14}
                        style={{ marginLeft: 6, color: 'var(--danger)', verticalAlign: 'middle' }}
                        title={missingLabel}
                      />
                    )}
                    {isLate && !isIncomplete && (
                      <Clock
                        size={14}
                        style={{ marginLeft: 6, color: veryLate ? '#c0560f' : 'var(--warning)', verticalAlign: 'middle' }}
                        title={lateLabel}
                      />
                    )}
                  </td>
                  <td><span className="badge badge-info">{r.facility_ts}</span></td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {r.status_ts === 1
                        ? <span className="badge" style={{ background: 'var(--danger)', color: '#fff' }}>{t('ts.deleted') || 'Deleted'}</span>
                        : isIncomplete
                          ? <span className="badge" style={{ background: 'var(--danger)', color: '#fff' }} title={missingLabel}>{t('ts.incomplete') || 'Incomplete'}</span>
                          : isLate
                            ? <span className="badge" style={{ background: veryLate ? '#c0560f' : 'var(--warning)', color: '#fff' }} title={lateLabel}>{t('ts.late') || 'Late'} ({r.late_minutes}m)</span>
                            : <span className="badge badge-success">{t('common.active') || 'Active'}</span>}
                      {/* Origin of the punch, orthogonal to the status above: a hand-made punch is
                          paid with the time it says (no shift rounding) and survives a re-upload
                          of the week's report, so it is worth seeing without opening the row. */}
                      {r.is_manual && (
                        <span className="badge" style={{ background: '#7c3aed', color: '#fff' }}
                          title={t('ts.manualTooltip') || 'Captured or corrected by hand: paid with the time shown (no rounding) and kept when the report is re-uploaded.'}>
                          {t('ts.manual') || 'Manual'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {r.status_ts === 0 && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-icon" title={t('common.edit') || 'Edit'} onClick={() => openEdit(r)}><Pencil size={14} /></button>
                        <button className="btn-icon" title={t('common.delete') || 'Delete'} style={{ color: 'var(--danger)' }} onClick={() => handleDelete(r)}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > pageSize && (
          <div style={{ marginTop: 12 }}>
            <Pagination page={page} limit={pageSize} total={total} onPageChange={p => fetchData(p)} />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} large={!editing}
        title={editing ? (t('ts.editTitle') || 'Edit punch') : (t('ts.addTitle') || 'Add manual punch')}>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            {/* 1. A QUIEN — el empleado y su facilidad mandan sobre todo el lote. */}
            <div className="msection">
              <div className="msection-head"><span>{t('ts.secWho')}</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, alignItems: 'end' }}>
                <div style={{ position: 'relative' }} ref={empDropdownRef}>
                  <label className="form-label">{t('hrs.employee') || 'Employee'} *</label>
                  {empSelected ? (
                    // Ya elegido: se muestra como un hecho, no como una caja de busqueda a medio
                    // llenar. El boton devuelve el buscador.
                    <div className="picked-emp">
                      <span className="badge badge-info" style={{ fontSize: 11 }}>{empSelected.badge_num_emp}</span>
                      <span style={{ fontWeight: 600 }}>{empSelected.name_emp} {empSelected.last_name_emp}</span>
                      <span className="badge badge-info" style={{ fontSize: 10 }}>{empSelected.location_emp}</span>
                      <button type="button" className="row-ghost-btn" style={{ marginLeft: 'auto' }}
                        title={t('ts.changeEmp')}
                        onClick={() => {
                          // Tambien se limpia el badge del form: si no, quitar al empleado dejaba
                          // el anterior pegado y el guardado se iba con quien ya no esta a la vista.
                          setEmpSelected(null); setEmpSearch(''); setEmpResults([]);
                          setForm(f => ({ ...f, badgenum_ts: '' }));
                        }}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <input className="form-input" style={{ marginBottom: 0 }} value={empSearch}
                      onChange={e => onEmpSearch(e.target.value)}
                      onFocus={() => { if (empResults.length > 0 && !empSelected) setShowEmpDropdown(true); }}
                      placeholder={t('ts.searchPh') || 'Name or badge'} />
                  )}
                  {showEmpDropdown && empResults.length > 0 && !empSelected && (
                    <div className="export-search-dropdown" style={{ position: 'absolute', top: '100%', left: 0, right: 0, width: 'auto', zIndex: 100 }}>
                      {empResults.map(emp => (
                        <div key={emp.id_emp} className="export-search-item" onClick={() => onEmpPick(emp)}>
                          <span style={{ fontWeight: 600 }}>{emp.badge_num_emp}</span>
                          <span style={{ marginLeft: 8 }}>{emp.name_emp} {emp.last_name_emp}</span>
                          <span className="badge badge-info" style={{ marginLeft: 'auto', fontSize: 10 }}>{emp.location_emp}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="form-label">{t('hrs.facility') || 'Facility'} *</label>
                  <select className="form-input" style={{ marginBottom: 0 }} value={form.facility_ts}
                    onChange={e => setForm(f => ({ ...f, facility_ts: e.target.value }))}>
                    <option value="">{t('common.select') || 'Select'}</option>
                    <option value="MIA">MIA</option>
                    <option value="AUSWH">AUSWH</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 2. QUE — un renglon por punch. */}
            <div className="msection">
              <div className="msection-head">
                <span>{editing ? (t('ts.punch') || 'Punch') : (t('ts.punchesLabel') || 'Punches to add')}</span>
                {!editing && filledLines > 0 && (
                  <span className="msection-count">{filledLines}</span>
                )}
              </div>
              {!editing && <p className="msection-hint">{t('ts.linesHint')}</p>}

              <div className="rowtable">
                <div className="rowtable-head" style={LINE_GRID}>
                  <span>{t('common.date') || 'Date'} *</span>
                  <span>{t('common.time') || 'Time'} *</span>
                  <span>{t('rec.action') || 'Action'} *</span>
                  <span>{t('rec.door') || 'Door'} *</span>
                  <span />
                </div>

                {lines.map((l, i) => (
                  <div key={i} className={`rowtable-row${l.hour_ts ? ' is-edited' : ''}`} style={LINE_GRID}>
                    <DateInput className="form-input"
                      value={l.date_ts} onChange={v => setLine(i, { date_ts: v })} />
                    <input type="time" className="form-input"
                      value={l.hour_ts ? l.hour_ts.substring(0, 5) : ''}
                      onChange={e => setLine(i, { hour_ts: e.target.value ? `${e.target.value}:00` : '' })} />
                    <select className="form-input"
                      value={l.action_ts} onChange={e => setLine(i, { action_ts: e.target.value })}>
                      <option value="IN">IN</option>
                      <option value="OUT">OUT</option>
                    </select>
                    <select className="form-input"
                      value={l.door_name_ts || ''} onChange={e => onDoorPick(i, e.target.value)}>
                      <option value="">{t('common.select') || 'Select'}</option>
                      {filteredDoors.map(d => (
                        <option key={d.name_door} value={d.name_door}>
                          {d.name_door} ({d.type_door}){d.facility ? ` — ${d.facility}` : ''}
                        </option>
                      ))}
                    </select>
                    {editing ? <span /> : (
                      <button type="button" onClick={() => removeLine(i)} disabled={lines.length <= 1}
                        title={t('ts.removeLine') || 'Remove this punch'} className="row-ghost-btn">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {!editing && (
                <button type="button" onClick={addLine} className="btn-secondary"
                  style={{ width: 'auto', padding: '6px 14px', fontSize: 12.5, marginTop: 10 }}>
                  <Plus size={14} style={{ marginRight: 6 }} />{t('ts.addLine') || 'Add another punch'}
                </button>
              )}
            </div>

            {/* Lo que YA hay en esos dias. Sin esto se captura a ciegas y el punch del archivo
                termina ganando el calculo sin que nadie lo vea. */}
            {!editing && existingCount > 0 && (
              <div className="msection">
                <div className="msection-head">
                  <AlertCircle size={13} style={{ color: 'var(--warning)' }} />
                  <span>{t('ts.existingTitle')}</span>
                  <span className="msection-count">{existingCount}</span>
                  {loadingDayPunches && <span style={{ fontWeight: 400, textTransform: 'none' }}>…</span>}
                </div>
                <p className="msection-hint">{t('ts.existingHint')}</p>

                {Object.keys(dayPunches).sort().map(date => (
                  <div key={date} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      {date}
                    </div>
                    <div className="rowtable">
                      {dayPunches[date].map(p => {
                        const isManual = !!Number(p.manual_ts);
                        // Por nombre, no por numero: en AUSWH todas valen 0 y buscar por numero
                        // mostraba el tipo de otra puerta. Ver onDoorPick.
                        const door = doors.find(d => d.name_door === p.door_name_ts);
                        return (
                          <div key={p.id_ts} className="rowtable-row" style={EXISTING_GRID}>
                            <span className="cell-fixed">{fmtHour(p.hour_ts)}</span>
                            <span className="cell-label" style={{ fontWeight: 500 }}>
                              {p.door_name_ts || '—'}
                            </span>
                            <span className="cell-note" style={{ fontStyle: 'normal' }}>
                              {door?.type_door || ''}
                            </span>
                            <span className="rowchips">
                              <span className={`rowchip ${isManual ? 'rowchip-info' : 'rowchip-warn'}`}>
                                {isManual ? t('ts.tagManual') : t('ts.tagImported')}
                              </span>
                            </span>
                            <button type="button" className="btn-secondary"
                              style={{ width: 'auto', padding: '3px 10px', fontSize: 11.5 }}
                              title={t('ts.correctTip')}
                              onClick={() => correctExisting(p)}>
                              <Pencil size={12} style={{ marginRight: 4 }} />{t('ts.correct')}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 3. POR QUE — obligatorio al editar, opcional al capturar. */}
            <div className="msection">
              <div className="msection-head">
                <span>{t('ts.reasonLabel') || 'Reason'}</span>
                {editing && <span style={{ color: 'var(--danger)' }}>*</span>}
              </div>
              <textarea className="form-input" rows={2} style={{ marginBottom: 0 }} value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={editing ? (t('ts.reasonEditPh') || 'Why is this being edited?') : (t('ts.reasonCreatePh') || 'Optional — e.g. employee forgot to clock in')} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel') || 'Cancel'}</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} disabled={saving}>
              <Save size={15} style={{ marginRight: 6 }} />
              {saving
                ? (t('common.saving') || 'Saving...')
                : (!editing && filledLines > 1
                  ? `${t('common.save') || 'Save'} ${filledLines} ${t('ts.punches') || 'punches'}`
                  : (t('common.save') || 'Save'))}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
